import { useState, useCallback } from 'react';
import axios from 'axios';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Lock, Check, ArrowRight, Globe, Shield, Database, Users, Zap, ChevronRight } from 'lucide-react';
import { CATALOG_ENGINE_COUNT } from '@/lib/engineCount';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MATRIX_ROWS = [
  { feature: 'Patient-Owned Portable Health Vault (PPHV)', pai: true, traditional: false, openEvidence: false },
  { feature: 'Multilingual Voice + Text Intake (6 languages)', pai: true, traditional: false, openEvidence: false },
  { feature: `${CATALOG_ENGINE_COUNT}-Engine Clinical Intelligence (Rule-Based)`, pai: true, traditional: false, openEvidence: true },
  { feature: '24/7 Patient AI Agent (Non-Diagnostic)', pai: true, traditional: false, openEvidence: false },
  { feature: 'Drug Interaction + CYP450 Engine', pai: true, traditional: false, openEvidence: true },
  { feature: 'Supplement Safety + Knowledge Graph', pai: true, traditional: false, openEvidence: false },
  { feature: 'Topol-Swisher Longevity Intelligence', pai: true, traditional: false, openEvidence: false },
  { feature: 'Pregnancy Safety Shield', pai: true, traditional: false, openEvidence: false },
  { feature: '911 Emergency Escalation + Mock SMS', pai: true, traditional: true, openEvidence: false },
  { feature: 'Pediatric + Geriatric Age-Routing', pai: true, traditional: false, openEvidence: false },
  { feature: 'ICD-10 + CPT/E&M Draft Suggestions', pai: true, traditional: true, openEvidence: true },
  { feature: 'SOAP Note Generation (Physician-Ready)', pai: true, traditional: true, openEvidence: true },
  { feature: 'Blockchain-Enabled Health Mart (Planned)', pai: true, traditional: false, openEvidence: false },
  { feature: 'Free Forever for Patients', pai: true, traditional: false, openEvidence: false },
  { feature: '80/10 Barbell (Utah $4 + Open Evidence)', pai: true, traditional: false, openEvidence: true },
  { feature: 'Global Tropical Disease Coverage', pai: true, traditional: false, openEvidence: false },
];

function GreenTick() {
  return (
    <div className="w-6 h-6 rounded bg-emerald-500 flex items-center justify-center">
      <Check className="w-4 h-4 text-white" strokeWidth={3} />
    </div>
  );
}
function RedX() {
  return <div className="w-6 h-6 rounded bg-slate-200 flex items-center justify-center text-slate-400 text-xs font-bold">-</div>;
}

export default function InvestorSuite({ open, onClose }) {
  const [stage, setStage] = useState(1);
  const [leadForm, setLeadForm] = useState({ name: '', firm: '', email: '' });
  const [passcode, setPasscode] = useState('');
  const [error, setError] = useState('');
  const [verified, setVerified] = useState(false);

  const handleLeadSubmit = useCallback(async () => {
    if (!leadForm.name.trim() || !leadForm.firm.trim() || !leadForm.email.trim()) {
      setError('Name, firm, and email are required.'); return;
    }
    setError('');
    try {
      await axios.post(`${API}/reports/save`, {
        encounterId: 'INVESTOR-LEAD',
        reportData: { type: 'investor_lead', ...leadForm, timestamp: new Date().toISOString() }
      });
    } catch (err) {
      // Log to console for production debugging; do NOT block the investor from proceeding
      // (lead capture is best-effort; the passcode flow is independent).
      console.warn('[InvestorSuite] Lead save failed (continuing to passcode):', err?.message || err);
    }
    setStage(3);
  }, [leadForm]);

  const handlePasscodeVerify = useCallback(async () => {
    try {
      await axios.post(`${API}/investor-docs/unlock`, {
        passcode: passcode.trim(),
        name: leadForm.name.trim(),
        email: leadForm.email.trim(),
        organization: leadForm.firm.trim(),
      });
      setError(''); setVerified(true);
    } catch (err) {
      setError(err?.response?.status === 400
        ? (err?.response?.data?.detail || 'Please provide your name, firm, and a valid email.')
        : 'Invalid passcode.');
    }
  }, [passcode, leadForm]);

  const handleReset = useCallback(() => {
    setStage(1); setLeadForm({ name: '', firm: '', email: '' });
    setPasscode(''); setError(''); setVerified(false);
  }, []);

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) { onClose(); handleReset(); } }}>
      <DialogContent className="max-w-4xl max-h-[90vh] p-0 gap-0 overflow-hidden" style={{ zIndex: 9000 }} data-testid="investor-suite">

        {!verified ? (
          <div className="p-8 flex flex-col items-center justify-center min-h-[400px]">
            <Lock className="w-10 h-10 text-[#2563EB] mb-4" />
            <h2 className="text-2xl font-bold text-black mb-1">Investor Access</h2>
            <p className="text-sm text-slate-500 mb-6">
              <span className="text-[#EF4444] font-bold">P.AI</span> Global Health — Confidential
            </p>

            {stage === 1 && (
              <div className="w-full max-w-sm space-y-3" data-testid="investor-lead-form">
                <div><Label className="text-sm font-bold">Full Name *</Label>
                  <Input value={leadForm.name} onChange={e => setLeadForm(p => ({...p, name: e.target.value}))}
                    className="mt-1 border-2 border-slate-300" data-testid="inv-name" /></div>
                <div><Label className="text-sm font-bold">Firm Name *</Label>
                  <Input value={leadForm.firm} onChange={e => setLeadForm(p => ({...p, firm: e.target.value}))}
                    className="mt-1 border-2 border-slate-300" data-testid="inv-firm" /></div>
                <div><Label className="text-sm font-bold">Work Email *</Label>
                  <Input value={leadForm.email} onChange={e => setLeadForm(p => ({...p, email: e.target.value}))}
                    type="email" className="mt-1 border-2 border-slate-300" data-testid="inv-email" /></div>
                {error && <p className="text-red-600 text-sm">{error}</p>}
                <Button onClick={handleLeadSubmit} className="w-full bg-[#2563EB] hover:bg-blue-700 rounded-md" data-testid="inv-lead-submit">
                  Continue <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            )}

            {stage === 3 && (
              <div className="w-full max-w-sm space-y-3" data-testid="investor-passcode-form">
                <p className="text-sm text-slate-600 text-center">Enter your investor passcode to continue.</p>
                <div><Label className="text-sm font-bold">Passcode</Label>
                  <Input value={passcode} onChange={e => setPasscode(e.target.value)} type="password"
                    className="mt-1 border-2 border-slate-300" data-testid="inv-passcode" /></div>
                {error && <p className="text-red-600 text-sm">{error}</p>}
                <Button onClick={handlePasscodeVerify} className="w-full bg-[#2563EB] hover:bg-blue-700 rounded-md" data-testid="inv-passcode-submit">
                  Access Investor Suite <Shield className="w-4 h-4 ml-1" />
                </Button>
              </div>
            )}
          </div>
        ) : (
          <ScrollArea className="max-h-[85vh]">
            <div className="p-6 md:p-8">
              {/* Header */}
              <div className="text-center mb-8">
                <h1 className="text-3xl sm:text-4xl font-bold text-black mb-1">Universal Healthcare</h1>
                <p className="text-lg text-slate-600">Infrastructure OS | Multi-Modal, Multi-Agentic AI Orchestration</p>
                <div className="flex items-center justify-center gap-4 mt-4">
                  <span className="text-xl font-extrabold"><span className="text-[#EF4444]">P.AI</span> <span className="text-black">Global Health</span> <span className="text-slate-500 text-sm">(Patient A.i)</span></span>
                  <span className="text-slate-300">|</span>
                  <span className="text-xl font-extrabold"><span className="text-[#EF4444]">P.AI</span> <span className="text-black">Health Mart</span></span>
                </div>
              </div>

              {/* 1 Trillion Waters */}
              <div className="bg-blue-50 border-2 border-blue-200 rounded-2xl p-6 mb-8">
                <div className="flex items-center gap-2 mb-3">
                  <Zap className="w-6 h-6 text-[#2563EB]" />
                  <h3 className="text-xl font-bold text-black">The 1 Trillion Waters</h3>
                </div>
                <p className="text-sm text-slate-700 mb-3">
                  P.AI Global Health (Patient A.i) targets the <span className="font-bold text-[#2563EB]">$1 Trillion</span> in annual healthcare waste — 
                  frozen in administrative overhead, fragmented diagnostics, and siloed patient data trapped in hospital EHRs and EMRs.
                </p>
                <p className="text-sm text-slate-700 mb-3">
                  By providing the <span className="font-bold">portable, verified Patient Vault</span>, we melt "frozen" administrative 
                  and diagnostic costs into fluid, high-liquidity <span className="font-bold text-[#2563EB]">Waters</span> of efficient health data — 
                  accessible to the patient, structured for the provider, and monetizable through the curated Health Mart ecosystem.
                </p>
                <p className="text-sm text-slate-600 italic">
                  The patient owns the data. The provider gets intelligence. The payers, pharma, and DME vendors pay for access. 
                  Health equity at population scale.
                </p>
              </div>

              {/* Sovereign Moat: Triple-Lock System */}
              <div className="mb-8" data-testid="sovereign-moat">
                <div className="text-center mb-6">
                  <h3 className="text-2xl font-bold text-black mb-1">The P.AI Global Health (Patient A.i) Sovereign Moat</h3>
                  <p className="text-lg font-semibold text-[#1E40AF]">The Triple-Lock System</p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                  {/* Pillar 1: Pull Economy */}
                  <div className="border-2 border-[#1E40AF] rounded-2xl p-5 bg-white">
                    <div className="w-10 h-10 rounded-xl bg-[#1E40AF] flex items-center justify-center mb-3">
                      <Lock className="w-5 h-5 text-white" />
                    </div>
                    <h4 className="font-bold text-black mb-1">Lock 1: The Pull Economy</h4>
                    <p className="text-xs text-[#1E40AF] font-semibold mb-2">Network Effect</p>
                    <p className="text-sm text-slate-700 mb-2">
                      Legacy systems rely on <span className="font-semibold">Institutional-Push</span>. P.AI Global Health (Patient A.i) captures the market through <span className="font-bold text-[#EF4444]">Patient-Pull</span>.
                    </p>
                    <p className="text-xs text-slate-600">
                      Once a patient secures their history in the Patient Vault, they own the "Clinical Gravity." 
                      Providers must integrate with P.AI Global Health (Patient A.i) to follow the patient, creating an unbreakable, user-driven network effect.
                    </p>
                  </div>

                  {/* Pillar 2: 1 Trillion Waters */}
                  <div className="border-2 border-[#1E40AF] rounded-2xl p-5 bg-white">
                    <div className="w-10 h-10 rounded-xl bg-[#1E40AF] flex items-center justify-center mb-3">
                      <Lock className="w-5 h-5 text-white" />
                    </div>
                    <h4 className="font-bold text-black mb-1">Lock 2: The 1 Trillion Waters</h4>
                    <p className="text-xs text-[#1E40AF] font-semibold mb-2">Financial Liquidity</p>
                    <p className="text-sm text-slate-700 mb-2">
                      We are the <span className="font-bold text-[#2563EB]">Global Liquidity Layer</span> for healthcare waste.
                    </p>
                    <p className="text-xs text-slate-600">
                      By providing a portable, verified "Surgical Baseline," we eliminate redundant diagnostics. 
                      We convert $1 Trillion in "Frozen" administrative friction into fluid, high-velocity health data. 
                      This makes P.AI Global Health (Patient A.i) a fiscal necessity for insurance payers.
                    </p>
                  </div>

                  {/* Pillar 3: Deterministic Safety */}
                  <div className="border-2 border-[#1E40AF] rounded-2xl p-5 bg-white">
                    <div className="w-10 h-10 rounded-xl bg-[#1E40AF] flex items-center justify-center mb-3">
                      <Lock className="w-5 h-5 text-white" />
                    </div>
                    <h4 className="font-bold text-black mb-1">Lock 3: Deterministic Safety</h4>
                    <p className="text-xs text-[#1E40AF] font-semibold mb-2">Deterministic Shield</p>
                    <p className="text-sm text-slate-700 mb-2">
                      Safety is not probabilistic; it is <span className="font-bold text-[#EF4444]">Hard-Coded</span>.
                    </p>
                    <p className="text-xs text-slate-600">
                      Unlike LLM-based competitors ($12B Open Evidence / $3.5B Hippocratic), P.AI Global Health (Patient A.i) uses Multi-Agentic AI Orchestration. 
                      Our 7-Step Pipeline ensures 0% logic drift on critical emergencies (e.g., Pediatric Cyanosis 911-trigger). 
                      This is a legal and clinical "Safe Harbor" moat.
                    </p>
                  </div>
                </div>

                {/* Moat Comparison Table */}
                <div className="overflow-x-auto">
                  <table className="w-full text-sm border-collapse">
                    <thead>
                      <tr className="border-b-2 border-[#1E40AF]">
                        <th className="text-left py-2.5 pr-4 text-[#1E40AF] font-bold">Strategic Moat</th>
                        <th className="text-center py-2.5 px-3 text-slate-600">Legacy Players</th>
                        <th className="text-center py-2.5 px-3 text-slate-600">AI Scribes</th>
                        <th className="text-center py-2.5 px-3"><span className="text-[#EF4444] font-extrabold">P.AI</span> <span className="font-bold">Global Health (The Ant)</span></th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-b border-slate-100">
                        <td className="py-2.5 pr-4 font-semibold text-slate-800">Data Control</td>
                        <td className="text-center py-2.5 text-slate-500">Siloed (Locked)</td>
                        <td className="text-center py-2.5 text-slate-500">Ephemeral (Lost)</td>
                        <td className="text-center py-2.5 font-bold text-emerald-700">Sovereign (Vault)</td>
                      </tr>
                      <tr className="border-b border-slate-100">
                        <td className="py-2.5 pr-4 font-semibold text-slate-800">Safety</td>
                        <td className="text-center py-2.5 text-slate-500">Human-Dependent</td>
                        <td className="text-center py-2.5 text-red-500">Probabilistic (Risky)</td>
                        <td className="text-center py-2.5 font-bold text-emerald-700">Deterministic (Shielded)</td>
                      </tr>
                      <tr className="border-b border-slate-100">
                        <td className="py-2.5 pr-4 font-semibold text-slate-800">Economic Flow</td>
                        <td className="text-center py-2.5 text-slate-500">Profit from Waste</td>
                        <td className="text-center py-2.5 text-slate-500">Incremental Gain</td>
                        <td className="text-center py-2.5 font-bold text-emerald-700">Waste Recovery (Waters)</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Competitive Matrix */}
              <div className="mb-8">
                <h3 className="text-xl font-bold text-black mb-4">Competitive Matrix — 2026</h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm border-collapse">
                    <thead>
                      <tr className="border-b-2 border-slate-300">
                        <th className="text-left py-2 pr-4 text-slate-700">Feature</th>
                        <th className="text-center py-2 px-3"><span className="text-[#EF4444] font-extrabold">P.AI</span> <span className="font-bold text-[#2563EB]">Patient Vault</span></th>
                        <th className="text-center py-2 px-3 text-slate-600">Traditional EHR</th>
                        <th className="text-center py-2 px-3 text-slate-600">Open Evidence ($12B)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {MATRIX_ROWS.map((row, i) => (
                        <tr key={i} className="border-b border-slate-100 hover:bg-slate-50">
                          <td className="py-2 pr-4 text-slate-800">{row.feature}</td>
                          <td className="text-center py-2">{row.pai ? <GreenTick /> : <RedX />}</td>
                          <td className="text-center py-2">{row.traditional ? <GreenTick /> : <RedX />}</td>
                          <td className="text-center py-2">{row.openEvidence ? <GreenTick /> : <RedX />}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Architecture Summary */}
              <div className="bg-slate-50 rounded-2xl p-6 mb-8 border border-slate-200">
                <h3 className="text-xl font-bold text-black mb-3">Architecture</h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
                  <div className="p-3 bg-white rounded-xl border border-slate-200">
                    <Database className="w-5 h-5 text-[#2563EB] mb-2" />
                    <div className="font-bold text-black">{CATALOG_ENGINE_COUNT} Clinical Engines</div>
                    <div className="text-slate-500 text-xs">Adult, Pediatric, Geriatric, Emergency, Behavioral, Women's, Sports, Tropical, Drug Safety, Supplements</div>
                  </div>
                  <div className="p-3 bg-white rounded-xl border border-slate-200">
                    <Users className="w-5 h-5 text-[#2563EB] mb-2" />
                    <div className="font-bold text-black">Patient-First Design</div>
                    <div className="text-slate-500 text-xs">6 Languages, Voice+Text, 24/7 AI Agent, Non-Diagnostic, Clinician-in-the-Loop</div>
                  </div>
                  <div className="p-3 bg-white rounded-xl border border-slate-200">
                    <Globe className="w-5 h-5 text-[#2563EB] mb-2" />
                    <div className="font-bold text-black">Global Scale</div>
                    <div className="text-slate-500 text-xs">USA + India, Tropical Disease, HIV/STI, Public Health, Blockchain Health Mart (Planned)</div>
                  </div>
                </div>
              </div>

              {/* Revenue Model */}
              <div className="mb-8">
                <h3 className="text-xl font-bold text-black mb-3">Revenue Architecture</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2 p-3 bg-emerald-50 rounded-xl border border-emerald-200">
                    <span className="font-bold text-emerald-700 w-24">Free Tier:</span>
                    <span className="text-slate-700">Patient Vault (PPHV) + Provider SOAP + Ambient Scribe</span>
                  </div>
                  <div className="flex items-center gap-2 p-3 bg-blue-50 rounded-xl border border-blue-200">
                    <span className="font-bold text-[#2563EB] w-24">Pay Wall:</span>
                    <span className="text-slate-700">Pharma (DTC) + Payers + Health Mart (DME, Home Health, Second Opinion) — referral fee structure</span>
                  </div>
                </div>
              </div>

              {/* Contact */}
              <div className="text-center pt-4 border-t border-slate-200">
                <p className="text-lg font-bold"><span className="text-[#EF4444]">P.AI</span> Global Health — <span className="text-[#EF4444]">PATENT PENDING</span></p>
                <a href="mailto:sadasivam.eniasivam@paiglobalhealth.com" className="text-[#2563EB] text-sm hover:underline">sadasivam.eniasivam@paiglobalhealth.com</a>
                <p className="text-xs text-slate-400 mt-1">paiglobalhealth.com</p>
              </div>
            </div>
          </ScrollArea>
        )}
      </DialogContent>
    </Dialog>
  );
}
